/**
 * 사람 · 역할 · 세부 권한. **`user.view` 를 가진 사람에게만 보인다.**
 *
 * 축이 셋이다.
 *
 * - **역할**(기획·QA·개발) — 직무다. 위아래가 없고 **이것으로 할 수 있는
 *   일이 갈리지 않는다.**
 * - **관리자** — 전부 통과한다. 이 화면에서 오가지 않는다.
 * - **세부 권한** — 실제로 가르는 것. 체크박스가 곧 표의 행이다.
 *
 * ⚠ **관리자 여부는 여기서 못 준다.** 설정(`SUPERADMIN_EMAILS`)이 로그인마다
 * 못박는 값이라, 여기서 주면 다음 로그인에 되돌아가 **화면이 거짓말을 한다.**
 *
 * ⚠ **화면이 카드를 숨기는 것은 방어가 아니다.** 막는 것은 서버다
 * (`require_perm`) — 숨긴 화면의 API 는 그대로 열려 있다.
 */

import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { Card, CardContent } from '@/components/ui/card';

import {
  errorMessage,
  listUsers,
  NotLoggedInError,
  ROLE_LABEL,
  setUserPermissions,
  setUserActive,
  setUserRole,
  type Member,
  type PermissionDef,
  type Role,
} from '../api';
import { Check, Radio } from '../ui/Choice';

/**
 * 기능을 걷은 묶음(2026-10-18). 서버의 권한 키와 DB 제약에는 아직 남아 있어
 * 목록으로 내려오지만, 켜도 아무 일도 안 하므로 체크박스를 그리지 않는다.
 */
const RETIRED_GROUPS = new Set(['wiki', 'github', 'deploy']);

export function PeopleCard({
  meId,
  canGrant,
  canSetRole,
  onError,
  onUnauthorized,
}: {
  meId: number;
  /** `user.grant` 를 가졌나. 없으면 체크박스가 **아예 안 뜬다.** */
  canGrant: boolean;
  /** `user.role` 을 가졌나. */
  canSetRole: boolean;
  onError: (message: string | null) => void;
  onUnauthorized: () => void;
}) {
  const [users, setUsers] = useState<Member[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [catalog, setCatalog] = useState<PermissionDef[]>([]);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [openId, setOpenId] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data = await listUsers();
        if (cancelled) return;
        setUsers(data.users);
        setRoles(data.roles);
        setCatalog(data.permissions);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof NotLoggedInError) {
          onUnauthorized();
          return;
        }
        onError(`사람 목록을 불러오지 못했다: ${errorMessage(err)}`);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [onError, onUnauthorized]);

  /* 묶음별로 갈라 둔다. **서버가 준 순서를 지킨다** — 화면이 다시 정렬하면
     서버에서 키를 더할 때 나오는 자리가 화면 코드에 딸린다. */
  const groups = useMemo(() => {
    const out: { key: string; label: string; items: PermissionDef[] }[] = [];
    for (const def of catalog) {
      if (RETIRED_GROUPS.has(def.group)) continue;
      const last = out[out.length - 1];
      if (last && last.key === def.group) last.items.push(def);
      else out.push({ key: def.group, label: def.group_label, items: [def] });
    }
    return out;
  }, [catalog]);

  async function guard(
    id: number,
    run: () => Promise<Member>,
    ok: (m: Member) => string,
  ) {
    if (busyId !== null) return;
    setBusyId(id);
    onError(null);
    try {
      const next = await run();
      setUsers((prev) => prev.map((u) => (u.id === next.id ? next : u)));
      toast.success(ok(next));
    } catch (err) {
      if (err instanceof NotLoggedInError) {
        onUnauthorized();
        return;
      }
      onError(errorMessage(err));
    } finally {
      setBusyId(null);
    }
  }

  async function changeRole(target: Member, role: Role) {
    if (target.role === role) return;
    await guard(
      target.id,
      () => setUserRole(target.id, role),
      (m) => `${m.display_name} · ${ROLE_LABEL[m.role]}`,
    );
  }

  async function toggleActive(target: Member) {
    await guard(
      target.id,
      () => setUserActive(target.id, !target.is_active),
      (m) => `${m.display_name} · ${m.is_active ? '다시 들어올 수 있음' : '못 들어옴'}`,
    );
  }

  async function togglePerm(target: Member, key: string, on: boolean) {
    /* **가진 것 전부**를 보낸다. 더하기·빼기로 주고받으면 화면이 놓친 변화가
       조용히 남는다 — 두 사람이 같은 화면을 열고 있을 때 그렇다. */
    const next = on
      ? [...target.permissions, key]
      : target.permissions.filter((k) => k !== key);
    await guard(
      target.id,
      () => setUserPermissions(target.id, next),
      (m) => `${m.display_name} · 권한 ${m.permissions.length}개`,
    );
  }

  return (
    <Card>
      <CardContent className="pt-s5">
        <ul className="m-0 flex list-none flex-col gap-s4 p-0">
          {users.map((u) => {
            const open = openId === u.id;
            const busy = busyId === u.id;
            const mine = u.id === meId;
            return (
              <li
                key={u.id}
                className="border-t border-line pt-s4 first:border-t-0 first:pt-0"
              >
                <div
                  className={`flex flex-wrap items-center justify-between gap-s3 ${
                    u.is_active ? '' : 'opacity-55'
                  }`}
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-s2">
                      <span className="text-meta text-ink">{u.display_name}</span>
                      {mine && <span className="text-micro text-ink-3">· 나</span>}
                      {!u.is_active && (
                        /* **끈 사람도 목록에 남는다** — 사라지면 되살릴 길이
                           없다. 흐리게 그리고 이 배지로 상태를 말한다. */
                        <span
                          className="rounded-sm px-s2 py-[1px] text-micro font-bold"
                          style={{
                            background: 'var(--st-gray-t)',
                            color: 'var(--st-gray-f)',
                          }}
                        >
                          못 들어옴
                        </span>
                      )}
                      {u.is_admin && (
                        /* 관리자는 **역할과 다른 축**이라 라디오 옆이 아니라
                           이름 옆에 선다 — 고를 수 있는 것처럼 보이면 안 된다. */
                        <span
                          className="rounded-sm px-s2 py-[1px] text-micro font-bold"
                          style={{
                            background: 'var(--brand-t)',
                            color: 'var(--brand-d)',
                          }}
                        >
                          관리자
                        </span>
                      )}
                    </div>
                    <div className="text-small break-all text-ink-3">{u.email}</div>
                  </div>

                  <div className="flex flex-wrap items-center gap-s2">
                    {canSetRole ? (
                      roles.map((role) => (
                        <Radio
                          key={role}
                          name={`role-${u.id}`}
                          label={`${u.display_name} · ${ROLE_LABEL[role]}`}
                          checked={u.role === role}
                          /* **비울 수 없다.** 역할은 언제나 하나여야 한다. */
                          nullable={false}
                          onChange={(next) => {
                            if (next) void changeRole(u, role);
                          }}
                        >
                          {ROLE_LABEL[role]}
                        </Radio>
                      ))
                    ) : (
                      <span className="text-meta text-ink-3">{ROLE_LABEL[u.role]}</span>
                    )}

                    {canGrant && (
                      <button
                        type="button"
                        onClick={() => setOpenId(open ? null : u.id)}
                        aria-expanded={open}
                        className="rounded-md border border-line px-s3 py-s2 text-meta text-ink-2 transition-colors hover:bg-sunk hover:text-ink max-[720px]:min-h-[44px]"
                      >
                        {/* 숫자를 함께 보여 준다 — 접힌 채로도 「몇 개 켜져
                            있나」를 알아야 열어 볼지 정할 수 있다. */}
                        권한 {u.is_admin ? '전부' : u.permissions.length}
                      </button>
                    )}

                    {/* 켜고 끄기. **자기 자신과 관리자에게는 안 그린다** —
                        서버가 409 로 막는 자리라 단추가 있으면 거짓말이다.
                        관리자를 내리려면 `SUPERADMIN_EMAILS` 에서 뺀다. */}
                    {canSetRole && !mine && !u.is_admin && (
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => void toggleActive(u)}
                        className="rounded-md border border-line px-s3 py-s2 text-meta text-ink-3 transition-colors hover:bg-sunk hover:text-ink max-[720px]:min-h-[44px]"
                      >
                        {u.is_active ? '내보내기' : '되살리기'}
                      </button>
                    )}
                  </div>
                </div>

                {canGrant && open && (
                  <div className="mt-s4 flex flex-col gap-s3 rounded-md border border-line bg-sunk p-s4">
                    {u.is_admin && (
                      <p className="m-0 text-small text-ink-2">
                        관리자: 모든 권한 허용
                      </p>
                    )}
                    {mine && (
                      <p className="m-0 text-small text-st-red-f">
                        본인 권한은 다른 관리자가 변경할 수 있습니다.
                      </p>
                    )}
                    {groups.map((group) => (
                      <div
                        key={group.key}
                        className="flex flex-wrap items-center gap-s2 border-t border-line pt-s3 first:border-t-0 first:pt-0"
                      >
                        {/* **소제목이 아니라 이름표다** — 왼쪽에 한 번 서고
                            나머지는 체크가 채운다. */}
                        <span className="w-[52px] flex-none text-meta text-ink-3">
                          {group.label}
                        </span>
                        {group.items.map((def) => (
                          <Check
                            key={def.key}
                            label={`${u.display_name} · ${def.label}`}
                            checked={u.permissions.includes(def.key)}
                            onChange={(on) => {
                              if (busy || mine) return;
                              void togglePerm(u, def.key, on);
                            }}
                          >
                            {def.label}
                          </Check>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
}
